// NPM
import { Helmet } from "react-helmet";
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isEmpty, omitBy, isNil, map, filter } from "lodash";
import { useParams } from "react-router-dom";

//HOOKS
import usePagination from "Hooks/pagination.hook";
import { useSession } from "Hooks/index";
import useQuery from "Hooks/query.hook";

// HELPERS
import { decode } from "Helpers/enc";
import { toaster } from "Helpers/toaster";

// COMPONENTS
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import { Tabs } from "@life-line-lab/components-react";
import { SettingListComponent } from "Components/order-management/shared/settings-list";
import {
  listOrderSettingByCustomer,
  updateCustomerSettingStatus,
} from "Services/customer-settings.service";
import "./customer-setting.scss";
import { toNum } from "Helpers/get-to-num";
import { getEnumOfOrderSettings } from "Helpers/order-settings-helper";
import { OrderSettingsEnum } from "Data/settings";

const CustomerOrderSetting = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { org } = useSession();
  const query = useQuery();
  const page = query.get("page") || 1;
  const tabKey = query.get("tabKey") || "0";
  const { id } = useParams<{ id: string }>();

  const [response, setResponse] = useState([]);
  const [tabActiveKey, setTabActiveKey] = useState(tabKey);
  const [isCustomerPage, setIsCustomerPage] = useState(true);

  const [currentListCount, setCurrentListCount] = useState(0);
  const { paginationComponent, itemPerPage, queryParams, setQueryParams } = usePagination({
    currentListCount,
  });

  const initialFilters = {
    id: decode(id),
    type: getEnumOfOrderSettings(tabActiveKey),
    pagination: {
      offset: queryParams.offset,
      limit: toNum(itemPerPage),
    },
  };
  const [filters, setFilters] = useState<any>(initialFilters);

  const tabKeyPaginationProps = {
    tabKey: tabKey,
    pathWithPage: {
      withTabKey: `/order-management/settings?tabKey=${tabKey}&page=${page}`,
      withoutTabKey: `/order-management/settings?page=${page}`,
    },
    pathWithoutPage: {
      withTabKey: `/order-management/settings?tabKey=${tabKey}`,
      withoutTabKey: "/order-management/settings",
    },
  };

  const onFilter = (filterInput) => {
    if (isEmpty(filterInput)) {
      setFilters(initialFilters);
      return;
    }
    const filterValue = {
      ...filters,
      orFields: filterInput.orFields || null,
    };
    setFilters(filterValue);
  };

  useEffect(() => {
    return () => {
      setResponse([]);
    };
  }, [tabActiveKey]);

  useEffect(() => {
    if (!isEmpty(filters)) {
      handleFetchOrderSettingsList();
    }
  }, [filters]);

  const handleFetchOrderSettingsList = async () => {
    const responseData = await listOrderSettingByCustomer(
      filters.id,
      filters.orFields,
      filters.type,
    );

    //TODO: TO FIX: Solve the issue of 'property error does not exist on type Partial<any>', where does the partial come from?
    if (responseData && !responseData.error) {
      setResponse(responseData);
      setCurrentListCount(responseData.length);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleActivateCustomerSetting = async (checked: boolean, versionID: string) => {
    // console.log(`switch to ${checked} in page`);
    const responseData = await updateCustomerSettingStatus(versionID, filters.id, checked);
    // console.log(responseData)
    if (responseData && !responseData.error) {
      toaster("success", t("toaster.updateSuccessful"));
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const tabs = [
    {
      tabKey: "0",
      label: t("label.specialCare"),
      component: (
        <SettingListComponent
          onFilter={onFilter}
          tabName={getEnumOfOrderSettings(tabActiveKey)}
          tab={OrderSettingsEnum.SPECIAL_CARE}
          isCustomerPage={isCustomerPage}
          data={response}
          hideModal={undefined}
          onAddCustomer={undefined}
          onCreateVersion={undefined}
          onUpdateVersion={undefined}
          onActivateCustomerSettings={handleActivateCustomerSetting}
        />
      ),
    },
    {
      tabKey: "1",
      label: t("label.specialConditions"),
      component: (
        <SettingListComponent
          onFilter={onFilter}
          tabName={getEnumOfOrderSettings(tabActiveKey)}
          tab={OrderSettingsEnum.SPECIAL_CONDITION}
          isCustomerPage={isCustomerPage}
          data={response}
          hideModal={undefined}
          onAddCustomer={undefined}
          onCreateVersion={undefined}
          onUpdateVersion={undefined}
          onActivateCustomerSettings={handleActivateCustomerSetting}
        />      
      ),
    },
    {
      tabKey: "2",
      label: t("label.serviceType"),
      component: (
        <SettingListComponent
          onFilter={onFilter}
          tabName={getEnumOfOrderSettings(tabActiveKey)}
          tab={OrderSettingsEnum.SERVICE_TYPE}
          isCustomerPage={isCustomerPage}
          data={response}
          hideModal={undefined}
          onAddCustomer={undefined}
          onCreateVersion={undefined}
          onUpdateVersion={undefined}
          onActivateCustomerSettings={handleActivateCustomerSetting}
        />
      ),
    },
    {
      tabKey: "3",
      label: t("label.deliveryType"),
      component: ( 
        <SettingListComponent
          onFilter={onFilter}
          tabName={getEnumOfOrderSettings(tabActiveKey)}
          tab={OrderSettingsEnum.DELIVERY_TYPE}
          isCustomerPage={isCustomerPage}
          data={response}
          hideModal={undefined}
          onAddCustomer={undefined}
          onCreateVersion={undefined}
          onUpdateVersion={undefined}
          onActivateCustomerSettings={handleActivateCustomerSetting}
        />
      ),
    },
    {
      tabKey: "4",
      label: t("label.locations"),
      component: (
        <SettingListComponent
          onFilter={onFilter}
          tabName={getEnumOfOrderSettings(tabActiveKey)}
          tab={OrderSettingsEnum.LOCATION}
          isCustomerPage={isCustomerPage}
          data={response}
          hideModal={undefined}
          onAddCustomer={undefined}
          onCreateVersion={undefined}
          onUpdateVersion={undefined}
          onActivateCustomerSettings={handleActivateCustomerSetting}
        />
      ),
    },
    // {
    //     tabKey: '5',
    //     label: t("label.rateTable"),
    //     component: (
    //         <ListingLayout
    //             listComponent=
    //             {<SettingListComponent
    //                 onFilter={onFilter}
    //                 tabName={getEnumOfOrderSettings(tabActiveKey)}
    //                 tab={'5'}
    //                 isCustomerPage={isCustomerPage}
    //                 data={response}
    //                 hideModal={undefined}
    //                 onAddCustomer={undefined}
    //                 onCreateVersion={undefined}
    //                 onUpdateVersion={undefined}
    //                 onActivateCustomerSettings={handleActivateCustomerSetting}
    //             />}
    //             {...tabKeyPaginationProps}
    //         />
    //     )
    // },
    {
      tabKey: "8",
      label: t("label.insurance"),
      component: (
        <SettingListComponent
          onFilter={onFilter}
          tab={OrderSettingsEnum.INSURANCE}
          tabName={OrderSettingsEnum.TYPE_OF_GOODS}
          isCustomerPage={isCustomerPage}
          data={response}
          hideModal={undefined}
          onAddCustomer={undefined}
          onCreateVersion={undefined}
          onUpdateVersion={undefined}
          onActivateCustomerSettings={handleActivateCustomerSetting}
        />
      ),
    },
  ];

  const handleTabClick = (key: string) => {
    // if(key == "6"){
    //     key = "8"
    // }
    let path = "";
    if (key === "0") {
      path = page
        ? `/order-management/customer/${id}/settings?page=${page}`
        : `/order-management/customer/${id}/settings`;
    } else {
      path = page
        ? `/order-management/customer/${id}/settings?tabKey=${key}&page=${page}`
        : `/order-management/customer/${id}/settings?tabKey=${key}`;
    }
    setFilters({ ...initialFilters, type: getEnumOfOrderSettings(key) });
    setTabActiveKey(key);
    history.push(path);
  };

  return (
    <div className="customer-order-setting">
      <Helmet>
        <title>{t("nav.customerManagement")}</title>
      </Helmet>
      <ModuleTitleBar
        titleText={t("nav.customerManagement")}
        haveSubtitle
        subtitleText={t("title.customerProfileSetup")}
      />
      <React.Fragment>
        <Tabs
          destroyInactiveTabPane={true}
          onTabClick={handleTabClick}
          defaultActiveKey="0"
          activeKey={tabActiveKey}
          tabBarGutter={25}
          tabs={tabs}
        />
      </React.Fragment>
      <div className="my-30">{paginationComponent}</div>
    </div>
  );
};

export default CustomerOrderSetting;
